import { useApiUrl, useNotification } from "@refinedev/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { axiosInstance } from "../../../authProvider";

type MessageAttachment = {
    id: string;
    isInternal?: boolean;
    name?: string;
    file: {
        path: string;
    };
};

export type OrderMessage = {
    id: number;
    content: string;
    createdAt: string;
    isRead?: boolean;
    sender?: {
        id: number;
        firstName?: string;
        lastName?: string;
        photo?: { path: string } | null;
    };
    attachments?: MessageAttachment[];
};

type MessagesPageResponse = {
    data: OrderMessage[];
    nextCursor: number | null;
    hasMore: boolean;
    unreadCount: number;
};

const DEFAULT_LIMIT = 10;

const sortMessagesAsc = (messages: OrderMessage[]): OrderMessage[] => {
    return [...messages].sort((left, right) => left.id - right.id);
};

export const useOrderMessages = (orderId?: string | number) => {
    const apiUrl = useApiUrl();
    const { open } = useNotification();
    const [messages, setMessages] = useState<OrderMessage[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [nextCursor, setNextCursor] = useState<number | null>(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoadingInitial, setIsLoadingInitial] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const isInitialFetchInFlightRef = useRef(false);

    const fetchPage = useCallback(
        async (before?: number): Promise<MessagesPageResponse | null> => {
            if (!orderId) {
                return null;
            }
            const response = await axiosInstance.get<MessagesPageResponse>(
                `${apiUrl}/orders/${orderId}/messages`,
                {
                    params: {
                        limit: DEFAULT_LIMIT,
                        ...(before ? { before } : {}),
                    },
                },
            );
            return response.data;
        },
        [apiUrl, orderId],
    );

    const refreshFirstPage = useCallback(async () => {
        if (!orderId || isInitialFetchInFlightRef.current) {
            return;
        }
        isInitialFetchInFlightRef.current = true;
        setIsLoadingInitial(true);
        try {
            const firstPage = await fetchPage();
            if (!firstPage) {
                return;
            }
            setMessages(sortMessagesAsc(firstPage.data));
            setHasMore(firstPage.hasMore);
            setNextCursor(firstPage.nextCursor);
            setUnreadCount(firstPage.unreadCount ?? 0);
        } catch {
            open?.({
                type: "error",
                message: "Unable to load order messages",
            });
        } finally {
            setIsLoadingInitial(false);
            isInitialFetchInFlightRef.current = false;
        }
    }, [fetchPage, open, orderId]);

    const loadOlder = useCallback(async () => {
        if (!orderId || !hasMore || isLoadingMore || !nextCursor) {
            return;
        }
        setIsLoadingMore(true);
        try {
            const olderPage = await fetchPage(nextCursor);
            if (!olderPage) {
                return;
            }
            setMessages((previousMessages) =>
                sortMessagesAsc([...olderPage.data, ...previousMessages]),
            );
            setHasMore(olderPage.hasMore);
            setNextCursor(olderPage.nextCursor);
        } catch {
            open?.({
                type: "error",
                message: "Unable to load older messages",
            });
        } finally {
            setIsLoadingMore(false);
        }
    }, [fetchPage, hasMore, isLoadingMore, nextCursor, open, orderId]);

    const appendLocal = useCallback((newMessage: OrderMessage) => {
        setMessages((previousMessages) => {
            const filtered = previousMessages.filter((message) => message.id !== newMessage.id);
            return sortMessagesAsc([...filtered, newMessage]);
        });
        if (newMessage.isRead === false) {
            setUnreadCount((previousCount) => previousCount + 1);
        }
    }, []);

    const prependLocal = useCallback((olderMessages: OrderMessage[]) => {
        setMessages((previousMessages) =>
            sortMessagesAsc([...olderMessages, ...previousMessages]),
        );
    }, []);

    const markAllRead = useCallback(async () => {
        if (!orderId) {
            return;
        }
        await axiosInstance.patch(`${apiUrl}/orders/${orderId}/messages/read-all`);
        setMessages((previousMessages) =>
            previousMessages.map((message) => ({ ...message, isRead: true })),
        );
        setUnreadCount(0);
    }, [apiUrl, orderId]);

    useEffect(() => {
        if (!orderId) {
            setMessages([]);
            setHasMore(false);
            setNextCursor(null);
            setUnreadCount(0);
            return;
        }
        void refreshFirstPage();
    }, [orderId, refreshFirstPage]);

    return useMemo(
        () => ({
            messages,
            hasMore,
            unreadCount,
            isLoadingInitial,
            isLoadingMore,
            loadOlder,
            prependLocal,
            appendLocal,
            refreshFirstPage,
            markAllRead,
        }),
        [
            appendLocal,
            hasMore,
            isLoadingInitial,
            isLoadingMore,
            loadOlder,
            markAllRead,
            messages,
            prependLocal,
            refreshFirstPage,
            unreadCount,
        ],
    );
};
